/**
 * PilotLeague - Luxe Theme
 * 
 * Style: Premium, élégant, or et noir
 * Cible: Utilisateurs premium, 35-55+ ans
 * Inspiration: Rolex, First Class, Amex Centurion
 */

export const luxeTheme = {
  name: 'luxe',
  label: 'Luxe',
  
  // ============================================================================
  // ÉLÉMENTS COMMUNS (partagés dark/light)
  // ============================================================================
  common: {
    // Rayons de bordure (Luxe: élégants - rounded-2xl)
    radius: {
      sm: 'rounded-lg',
      md: 'rounded-xl',
      lg: 'rounded-2xl',
      full: 'rounded-full',
    },
    // Transitions (Luxe: fluides)
    transition: {
      fast: 'transition-all duration-200',
      normal: 'transition-all duration-300',
      slow: 'transition-all duration-500',
    },
    // Ombres (Luxe: dorées)
    shadow: {
      sm: 'shadow-sm',
      md: 'shadow-md',
      lg: 'shadow-lg',
      glow: 'shadow-lg shadow-amber-500/20',
    },
    // Typographie Luxe: Light, élégant, uppercase labels
    typography: {
      heading: 'font-light',              // font-weight: 300
      body: 'font-normal',                // font-weight: 400
      label: 'font-medium',               // font-weight: 500
      tracking: 'tracking-tight',         // titres
      trackingLabel: 'tracking-[0.15em]', // labels uppercase
      uppercase: true,                    // Labels en MAJUSCULES
    },
  },

  // ============================================================================
  // MODE SOMBRE
  // ============================================================================
  dark: {
    name: 'dark',
    
    // Backgrounds (noir profond)
    bg: {
      primary: 'bg-neutral-950',
      secondary: 'bg-neutral-900',
      tertiary: 'bg-neutral-800',
      card: 'bg-gradient-to-br from-neutral-900 via-neutral-900 to-neutral-800',
      cardHover: 'hover:bg-neutral-800/60',
      cardAccent: 'bg-gradient-to-br from-amber-950/30 via-neutral-900 to-neutral-900',
      input: 'bg-neutral-900/80',
      nav: 'bg-neutral-950/95 backdrop-blur-2xl',
      modal: 'bg-neutral-900',
      overlay: 'bg-black/80',
    },
    
    // Textes
    text: {
      primary: 'text-white',
      secondary: 'text-neutral-200',
      tertiary: 'text-neutral-400',
      muted: 'text-neutral-500',
      inverse: 'text-neutral-950',
      accent: 'text-amber-400',
      success: 'text-emerald-400',
      warning: 'text-amber-400',
      danger: 'text-rose-400',
      info: 'text-sky-400',
    },
    
    // Bordures (or subtil)
    border: {
      primary: 'border-neutral-800',
      secondary: 'border-neutral-800/50',
      accent: 'border-amber-500/30',
      accentStrong: 'border-amber-500/50',
      success: 'border-emerald-500/30',
      danger: 'border-rose-500/30',
    },
    
    // Accents & Gradients (or)
    accent: {
      primary: 'bg-gradient-to-r from-amber-600 via-amber-400 to-amber-600',
      primaryHover: 'hover:from-amber-500 hover:via-amber-300 hover:to-amber-500',
      primaryText: 'text-amber-400',
      gradient: 'bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500',
      gradientText: 'bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 bg-clip-text text-transparent',
      subtle: 'bg-gradient-to-r from-amber-500/20 via-yellow-500/10 to-amber-500/20',
    },
    
    // États
    states: {
      success: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
      warning: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
      danger: 'bg-rose-500/10 border-rose-500/30 text-rose-400',
      info: 'bg-sky-500/10 border-sky-500/30 text-sky-400',
    },
    
    // Scores
    score: {
      excellent: 'text-emerald-400',
      good: 'text-amber-400',
      average: 'text-neutral-300',
      poor: 'text-rose-400',
    },
    
    // Couleurs HEX pour jauges SVG
    scoreHex: {
      excellent: '#10b981', // emerald-500 >= 85
      good: '#fbbf24',      // amber-400 >= 70 (gold)
      average: '#d4d4d4',   // neutral-300 >= 50
      poor: '#fb7185',      // rose-400 < 50
    },
    
    // Tiers premium (Luxe style - Diamond/Platinum/Gold/Member)
    rarity: {
      legendary: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400',    // 💎 Diamond
      epic: 'bg-amber-500/20 border-amber-500/30 text-amber-400',      // ⭐ Platinum
      rare: 'bg-amber-900/20 border-amber-700/40 text-amber-500',      // 🥇 Gold
      common: 'bg-neutral-800 border-neutral-700 text-neutral-400',    // Member
    },
  },

  // ============================================================================
  // MODE CLAIR
  // ============================================================================
  light: {
    name: 'light',
    
    // Backgrounds (blanc cassé élégant)
    bg: {
      primary: 'bg-stone-50',
      secondary: 'bg-white',
      tertiary: 'bg-stone-100',
      card: 'bg-white',
      cardHover: 'hover:bg-stone-50',
      cardAccent: 'bg-gradient-to-br from-amber-50 via-white to-white',
      input: 'bg-white',
      nav: 'bg-white/95 backdrop-blur-2xl',
      modal: 'bg-white',
      overlay: 'bg-black/50',
    },
    
    // Textes
    text: {
      primary: 'text-neutral-900',
      secondary: 'text-neutral-700',
      tertiary: 'text-neutral-500',
      muted: 'text-neutral-400',
      inverse: 'text-white',
      accent: 'text-amber-600',
      success: 'text-emerald-600',
      warning: 'text-amber-600',
      danger: 'text-rose-600',
      info: 'text-sky-600',
    },
    
    // Bordures
    border: {
      primary: 'border-stone-200',
      secondary: 'border-stone-100',
      accent: 'border-amber-400/40',
      accentStrong: 'border-amber-500/60',
      success: 'border-emerald-300',
      danger: 'border-rose-300',
    },
    
    // Accents & Gradients
    accent: {
      primary: 'bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600',
      primaryHover: 'hover:from-amber-700 hover:via-amber-600 hover:to-amber-700',
      primaryText: 'text-amber-600',
      gradient: 'bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500',
      gradientText: 'bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600 bg-clip-text text-transparent',
      subtle: 'bg-gradient-to-r from-amber-100 via-amber-50 to-amber-100',
    },
    
    // États
    states: {
      success: 'bg-emerald-50 border-emerald-200 text-emerald-700',
      warning: 'bg-amber-50 border-amber-200 text-amber-700',
      danger: 'bg-rose-50 border-rose-200 text-rose-700',
      info: 'bg-sky-50 border-sky-200 text-sky-700',
    },
    
    // Scores
    score: {
      excellent: 'text-emerald-600',
      good: 'text-amber-600',
      average: 'text-neutral-600',
      poor: 'text-rose-600',
    },
    
    // Couleurs HEX pour jauges SVG
    scoreHex: {
      excellent: '#059669', // emerald-600 >= 85
      good: '#d97706',      // amber-600 >= 70 (gold)
      average: '#525252',   // neutral-600 >= 50
      poor: '#e11d48',      // rose-600 < 50
    },
    
    // Tiers premium (Luxe style - Diamond/Platinum/Gold/Member)
    rarity: {
      legendary: 'bg-cyan-50 border-cyan-300 text-cyan-700',           // 💎 Diamond
      epic: 'bg-amber-100 border-amber-300 text-amber-700',             // ⭐ Platinum
      rare: 'bg-amber-50 border-amber-200 text-amber-600',              // 🥇 Gold
      common: 'bg-stone-100 border-stone-300 text-stone-600',           // Member
    },
  },

  // ============================================================================
  // CARACTÉRISTIQUES DU STYLE
  // ============================================================================
  features: {
    // Pas d'emojis
    useEmojis: false,
    
    // Style de langage
    language: 'elegant',
    
    // Labels et textes
    labels: {
      score: 'Score',
      xp: 'Points',
      level: 'Level',
      rank: 'Rank',
      landing: {
        excellent: 'Exceptional',
        good: 'Accomplished',
        average: 'Standard',
        poor: 'Developing',
      },
      achievements: {
        legendary: 'Diamond 💎',
        epic: 'Platinum ⭐',
        rare: 'Gold 🥇',
        common: 'Member',
      },
      greetings: {
        morning: 'Good morning',
        afternoon: 'Good afternoon',
        evening: 'Good evening',
      },
    },
    
    // Animations (subtiles)
    animations: {
      enabled: true,
      scoreReveal: true,
      xpGain: false,
      levelUp: true,
    },
    
    // Affichage
    showXP: false,
    showStreak: true,
    
    // Icônes (Lucide React élégantes)
    icons: {
      flight: 'Plane',
      landing: 'PlaneLanding',
      score: 'Target',
      trophy: 'Crown',
      medal: 'Crown',
      fire: 'Gem',
      star: 'Diamond',
      crown: 'Crown',
    },
    
    // Spécial Luxe
    typography: {
      weight: 'light', // font-light pour l'élégance
      tracking: 'wide', // tracking-wide
      uppercase: true, // Labels en majuscules
    },
  },
};

export default luxeTheme;
